import PublicIcon from '@mui/icons-material/Public';
import ShareIcon from '@mui/icons-material/Share';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import {
  Badge,
  Box,
  Card,
  Divider,
  Stack,
  SxProps,
  Tooltip,
} from '@mui/material';
import React from 'react';

import { BaseComponent } from '../Component';
import { EvoachLinkify } from '../EvoachLinkify';
import { EvoachLogo } from '../EvoachLogo';

import { CardImageEnum } from './CardImageEnum';
import { CardImageSize, CardSize } from './CardSizeEnums';
import {
  ObjectCardCreatorImages,
  ObjectCardCreatorImagesProps,
} from './ObjectCardCreatorImages';
import { ObjectCardFlags } from './ObjectCardFlags';

export enum CardIndicatorIcon {
  SHAREDMODULE,
  DEFAULTMODULE,
  NONE,
}

export interface ObjectCardComponent extends BaseComponent {
  type: 'objectCard';
  props: ObjectCardProps;
}
export interface ObjectCardProps {
  title: string;
  description: string;
  duration: string;
  createdBy?: string;
  headermenu?: any;
  id?: string;
  image?: CardImageEnum;
  imageSrc?: string;
  imageSize?: CardImageSize;
  cardSize?: CardSize;
  indicator?: CardIndicatorIcon;
  indicatorDefaultModuleText?: string;
  indicatorSharedModuleText?: string;
  createdByImages?: ObjectCardCreatorImagesProps;
  flags?: string[];
  labels?: string[];
  badgeText?: string;
}
export const ObjectCard: React.FC<ObjectCardProps> = ({
  title,
  description,
  duration,
  headermenu,
  id,
  image,
  imageSrc,
  imageSize = CardImageSize.LARGE,
  cardSize = CardSize.LARGE,
  indicator = CardIndicatorIcon.NONE,
  indicatorDefaultModuleText = '',
  indicatorSharedModuleText = '',
  createdByImages,
  flags,
  labels,
  badgeText = '',
}) => {
  const displayDescription = description;
  const baseHeight = cardSize;

  const height = baseHeight - 145 + imageSize + 'px';
  const width = cardSize === CardSize.LARGE ? '275px' : '220px';

  // true if there is an imageSrc set, no matter, what image says
  const hasExternalImageSrc = imageSrc !== undefined && imageSrc !== '';

  // true if there is no imageSrc but an image
  const isLegacyIcon = !hasExternalImageSrc && image !== undefined;
  const isIconWithUrl = hasExternalImageSrc && image !== undefined;

  const parsedImageUrl = hasExternalImageSrc ? `url(${imageSrc})` : undefined;

  // for better readibilioty, I extracted the sx part
  const sxObject: SxProps =
    isLegacyIcon || isIconWithUrl
      ? {
          backgroundImage: parsedImageUrl,
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'multiply',
          flex: '0 0 40%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 16px 0 16px',
          // difference
          backgroundSize: '45%',
          backgroundOrigin: undefined,
          backgroundPosition: 'bottom 30px right 50%;', //bottom 20px top 20px left 20px right 20px',
        }
      : {
          backgroundImage: parsedImageUrl,
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'multiply',
          flex: '0 0 40%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 16px 0 16px',
          // difference
          backgroundSize: 'cover',
          backgroundOrigin: undefined,
          backgroundPosition: 'center',
        };

  return (
    <Badge
      badgeContent={badgeText}
      invisible={badgeText === ''}
      color="secondary"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        '& .MuiBadge-badge':
          badgeText !== ''
            ? {
                transform: 'translate(-10px, -10px)',
              }
            : {},
      }}
    >
      <Card
        sx={{
          width: width,
          maxWidth: width,
          height: height,
          maxHeight: height,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          backgroundColor: '#F5F5F5',
        }}
        id={id + ''}
      >
        {/* Upper Part of Card */}
        <Box sx={sxObject}>
          <Box width="100%">{headermenu}</Box>
          <Box height="28px">
            {labels && (
              <Box
                sx={{
                  backgroundColor: 'gray',
                  borderRadius: '10px',
                  padding: '1px 10px',
                  typography: 'body2',
                  color: 'white',
                  width: 'fit-content',
                }}
              >
                {labels[0]}
              </Box>
            )}
          </Box>
        </Box>
        {/* Lower Part of Card */}
        <Box
          display="flex"
          flexDirection="column"
          padding="16px 16px 8px 16px"
          justifyContent="space-between"
          sx={{ flex: '1 0 15%', backgroundColor: 'white' }}
        >
          <Box
            sx={{ flex: '0 1 2em' }}
            typography="h6"
            fontWeight={600}
            lineHeight="normal"
            marginTop="-5px"
          >
            {title}
          </Box>
          <Box
            sx={{
              typography: 'body2',
              overflow: 'auto',
              textOverflow: 'ellipsis',
              flex: '1 0 1px',
              color: 'text.secondary',
              minHeight: '0',
              marginBottom: '0.5em',
              paddingTop: '0.5em',
            }}
            typography="body2"
          >
            <EvoachLinkify>{displayDescription}</EvoachLinkify>
          </Box>
          <Box
            sx={{
              typography: 'body2',
              overflow: 'auto',
              flex: '0 0 10%',
              width: '100%',
              display: 'inline-flex',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <WatchLaterIcon fontSize="inherit" color="inherit" />
            </Box>
            <Box
              sx={{
                flexBasis: '60%',
                marginLeft: '10px',
                flexGrow: 3,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
              typography="body2"
              fontSize={13}
              fontWeight={800}
            >
              {duration}
            </Box>
            <Box flex="0 0 auto">
              <ObjectCardFlags flags={flags} />
            </Box>
          </Box>
          {createdByImages ? (
            <Box
              sx={{
                flex: '0 0 1px',
              }}
            >
              <Divider sx={{ marginBottom: '8px', marginTop: '3px' }} />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Box display="flex" flexDirection="row">
                  {indicator === CardIndicatorIcon.DEFAULTMODULE && (
                    <Box marginRight="10px">
                      <EvoachLogo width={25} />
                    </Box>
                  )}
                  <Box
                    style={{
                      maxWidth: width,
                      overflow: 'hidden',
                    }}
                  >
                    <ObjectCardCreatorImages {...createdByImages} />
                  </Box>
                </Box>
                <div>
                  {indicator === CardIndicatorIcon.NONE && <></>}
                  {indicator === CardIndicatorIcon.DEFAULTMODULE && (
                    <Tooltip
                      title={indicatorDefaultModuleText}
                      placement="top"
                      arrow
                    >
                      <PublicIcon fontSize="small" color="primary" />
                    </Tooltip>
                  )}
                  {indicator === CardIndicatorIcon.SHAREDMODULE && (
                    <Tooltip
                      title={indicatorSharedModuleText}
                      placement="top"
                      arrow
                    >
                      <ShareIcon fontSize="small" color="primary" />
                    </Tooltip>
                  )}
                </div>
              </Stack>
            </Box>
          ) : null}
        </Box>
      </Card>
    </Badge>
  );
};
